import store from "../state/store"
import {
    logoutAction,
    updateAuth,
    updateAlert,
    refetchingToken,
    updateUpdatingToken,
    updateSettimeoutObject,
} from "../state/login/loginSlice"
import { navigate } from "gatsby"
import {
    healthInstance,
    journalInstance,
    authInstance,
    lambdaInstance,
    chatGPTInstance,
    gptInstance,
    twilioInstance,
    carePlanAiInstance,
    DoctoriteAiInstance,
    docAssistInstance,
    stripeInstance,
    googleCalendarInstance,
} from "./axios"
import queryString from "query-string"
import { authTokenURL } from "../apiEndPoints"
import { endLoginTimeTracker } from "../state"
import { userLoggedOut } from "../state/rootReducer"
import axios from "axios"

// The in-flight token refresh, shared by every caller (timer, 401 retries,
// tab focus) so only one refresh request runs at a time
let refreshPromise = null
const refreshAndRetryQueue = []
const ongoingRequests = new Map()
const MAX_RETRIES = 3
// Refresh this long before the access token expires
const REFRESH_EARLY_MS = 10 * 1000
// Attempts per refresh when the auth server can't be reached
const REFRESH_NETWORK_RETRIES = 3

const readStoredAuth = () => {
    try {
        return JSON.parse(localStorage.getItem("userAuth"))
    } catch (e) {
        return null
    }
}

const forceLogout = () => {
    localStorage.removeItem("userAuth")
    store.dispatch(endLoginTimeTracker(false))
    store.dispatch(logoutAction())
    store.dispatch(userLoggedOut())
    navigate("/login")
    store.dispatch(
        updateAlert({
            ...store.getState().login.alert,
            open: true,
            type: "error",
            msg: `Token expired please login again`,
        })
    )
}

const scheduleRefresh = auth => {
    const existing = store.getState().login.settimeoutObject
    if (existing) {
        try {
            clearTimeout(existing)
        } catch (e) {}
    }
    if (!auth?.refresh_token) return
    // No expires_at = saved before it was tracked, so refresh right away
    const delay = auth.expires_at
        ? Math.max(0, auth.expires_at - Date.now() - REFRESH_EARLY_MS)
        : 0
    const temp = setTimeout(() => {
        refreshToken()
    }, delay)
    store.dispatch(updateSettimeoutObject(temp))
}

// Stores a token response with its absolute expiry (so the refresh can be
// rescheduled after a page reload) and schedules the next refresh
const saveAuth = data => {
    const auth = { ...data }
    if (auth.expires_in)
        auth.expires_at = Date.now() + Number(auth.expires_in) * 1000
    store.dispatch(updateAuth(auth))
    localStorage.setItem("userAuth", JSON.stringify(auth))
    scheduleRefresh(auth)
}
const generateRequestId = config => {
    return (
        config.method.toUpperCase() +
        config.url +
        JSON.stringify(config.params || {}) +
        JSON.stringify(config.data || {})
    )
}
const onRequest = async config => {
    // const requestId = generateRequestId(config);

    // if (ongoingRequests.has(requestId)) {
    //     console.log('Duplicate request intercepted and cancelled:', config);
    //     return Promise.reject(new Error('Duplicate request intercepted and cancelled'));
    // }

    // ongoingRequests.set(requestId, true);
    if (refreshPromise) await refreshPromise
    const token = JSON.parse(localStorage.getItem("userAuth"))
    if (
        token &&
        token.hasOwnProperty("access_token") &&
        token.access_token !== ""
    ) {
        config.headers["userType"] = "patient"
        config.headers["Authorization"] = token.access_token
    }
    return config
}

const onRequestError = error => {
    return Promise.reject(error)
}

const onResponse = response => {
    return response
}

const makeOnResponseError = instance => async error => {
    console.log(error, "error in response error")
    const originalRequest = error.config

    // Check if it's the first retry attempt and not already retried
    if (originalRequest && !originalRequest._retry) {
        originalRequest._retry = true
        originalRequest._retryCount = 1
    }

    // Retry the request if we have not exceeded MAX_RETRIES
    if (
        originalRequest &&
        originalRequest._retryCount < MAX_RETRIES &&
        error.response
    ) {
        originalRequest._retryCount += 1
        console.log(
            `Retrying request (${originalRequest._retryCount}/${MAX_RETRIES})`
        )

        // Wait for some delay before retrying (optional)
        await new Promise(resolve => setTimeout(resolve, 500))

        // Retry the request
        switch (
            originalRequest.baseURL
                .split("https://")[0]
                .split(".")[0]
                .split("-")[0]
        ) {
            case "health": {
                healthInstance(originalRequest)
                break
            }
            case "journal": {
                journalInstance(originalRequest)
                break
            }
            case "auth": {
                authInstance(originalRequest)
                break
            }
        }
    }

    console.log("check if it comes here", error, originalRequest.baseUrl)
    if (error.response?.status === 401) {
        // The access token may just have expired (e.g. the timer was delayed
        // by sleep or a background tab) — refresh once and replay the request
        // before treating it as a real logout
        if (
            originalRequest &&
            !originalRequest._authRetried &&
            readStoredAuth()?.refresh_token
        ) {
            originalRequest._authRetried = true
            const refreshed = await refreshToken()
            if (refreshed) return instance(originalRequest)
        }
        // A rejected refresh has already logged out
        if (readStoredAuth()) forceLogout()
        return null
    }

    return Promise.reject(error)
}

healthInstance.interceptors.request.use(onRequest, onRequestError)
healthInstance.interceptors.response.use(
    onResponse,
    makeOnResponseError(healthInstance)
)

journalInstance.interceptors.request.use(async config => {
    if (refreshPromise) await refreshPromise
    const token = JSON.parse(localStorage.getItem("userAuth"))
    if (
        token &&
        token.hasOwnProperty("access_token") &&
        token.access_token !== ""
    ) {
        config.headers["userType"] = "patient"
        config.headers["Authorization"] = token.access_token
    }
    return config
}, onRequestError)
journalInstance.interceptors.response.use(
    onResponse,
    makeOnResponseError(journalInstance)
)

docAssistInstance.interceptors.request.use(onRequest, onRequestError)
docAssistInstance.interceptors.response.use(
    onResponse,
    makeOnResponseError(docAssistInstance)
)

// Only the request side: attaches the login token (and, in the client
// dashboard, userType) exactly like healthInstance. The health response
// handler is deliberately not reused — its retry/logout logic is keyed to the
// health/journal/auth hosts. Same for the Google Calendar service.
stripeInstance.interceptors.request.use(onRequest, onRequestError)
googleCalendarInstance.interceptors.request.use(onRequest, onRequestError)

authInstance.interceptors.request.use(
    async config => {
        // config.headers["Authorization"] = `Basic ${
        //     store.getState().key.authToken
        // }`
        // Same client credentials generateToken logs in with — a refresh
        // token is only accepted from the client it was issued to
        config.headers[
            "Authorization"
        ] = `Basic ${"cGF0aWVudC1hcHA6cGF0aWVudF9zZWNyZXRfa2V5"}`
        // The refresh token grant expects dashboard_user, not patient
        config.headers["UserType"] = "dashboard_user"
        config.headers["Content-Type"] = "application/x-www-form-urlencoded"
        return config
    },
    error => {
        return Promise.reject(error)
    }
)

const doRefresh = async refresh_token => {
    const data = {
        grant_type: "refresh_token",
        refresh_token,
    }
    for (let attempt = 1; ; attempt++) {
        try {
            const resp = await authInstance.post(
                authTokenURL,
                queryString.stringify(data)
            )
            console.log("refresh token console log", resp.data)
            saveAuth(resp.data)
            return resp.data
        } catch (e) {
            const status = e.response?.status
            // Network drop / server hiccup: retry, and if it's still down try
            // again shortly instead of logging the patient out over it
            if (!status || status >= 500) {
                if (attempt < REFRESH_NETWORK_RETRIES) {
                    await new Promise(resolve =>
                        setTimeout(resolve, 1000 * attempt)
                    )
                    continue
                }
                scheduleRefresh({
                    ...readStoredAuth(),
                    expires_at: Date.now() + REFRESH_EARLY_MS + 30 * 1000,
                })
                return null
            }
            // The auth server rejected the refresh token — the session is over
            forceLogout()
            return null
        }
    }
}

export const refreshToken = async (success = () => {}) => {
    const refresh_token = readStoredAuth()?.refresh_token
    if (!refresh_token) return null
    if (!refreshPromise) {
        refreshPromise = doRefresh(refresh_token).finally(() => {
            refreshPromise = null
        })
    }
    const result = await refreshPromise
    if (result) success()
    return result
}

// Run once when the app starts. The refresh timer only lives in memory, so
// without this a page reload stopped refreshing and the patient was logged
// out as soon as the current access token expired.
export const initSessionRefresh = () => {
    if (typeof window === "undefined") return
    const auth = readStoredAuth()
    if (auth?.refresh_token) scheduleRefresh(auth)

    // Timers are throttled in background tabs and paused while the device
    // sleeps, so check again whenever the patient comes back to the tab
    const checkSession = () => {
        if (document.visibilityState !== "visible") return
        const current = readStoredAuth()
        if (!current?.refresh_token) return
        if (
            !current.expires_at ||
            current.expires_at - Date.now() < REFRESH_EARLY_MS
        )
            refreshToken()
    }
    document.addEventListener("visibilitychange", checkSession)
    window.addEventListener("focus", checkSession)
}

export const generateToken = async (data, userType, success, failure) => {
    try {
        const formData = new FormData()
        formData.append("grant_type", data.grant_type)
        formData.append("username", data.username)
        formData.append("password", data.password)
        formData.append("dateOfBirth", data.dateOfBirth)
        var config = {
            method: "post",
            url: `${process.env.GATSBY_AUTH_API}${authTokenURL}`,
            headers: {
                UserType: "patient",
                loginType: "Phone",
                Authorization:
                    "Basic cGF0aWVudC1hcHA6cGF0aWVudF9zZWNyZXRfa2V5",
                "Content-Type": "application/x-www-form-urlencoded",
            },
            data: queryString.stringify(data),
        }
        const resp = await axios(config)
        // const resp = await authInstance.post(authTokenURL, formData, {
        //     headers: {
        //         "Content-Type": "multipart/form-data",
        //         UserType: "patient",
        //         LoginType: "Phone",
        //         Authorization: "Basic cGF0aWVudC1hcHA6cGF0aWVudF9zZWNyZXRfa2V5",
        //         "Content-Type": "application/x-www-form-urlencoded",
        //     },
        // })
        saveAuth(resp.data)
        success()
    } catch (e) {
        alert("Login failed", e.message)
        console.log("Error", e)
        failure()
    }
}

lambdaInstance.interceptors.response.use(
    async function (response) {
        console.log("<---- RESPONSE ---->", response)
        return Promise.resolve(response)
    },
    async function (error) {
        console.log("<---- ERROR ---->", error)
        return Promise.reject(error)
    }
)

chatGPTInstance.interceptors.response.use(
    async function (response) {
        console.log("<---- RESPONSE ---->", response)
        return Promise.resolve(response)
    },
    async function (error) {
        console.log("<---- ERROR ---->", error)
        return Promise.reject(error)
    }
)

gptInstance.interceptors.response.use(
    async function (response) {
        console.log("<---- RESPONSE ---->", response)
        return Promise.resolve(response)
    },
    async function (error) {
        console.log("<---- ERROR ---->", error)
        return Promise.reject(error)
    }
)

twilioInstance.interceptors.response.use(
    async function (response) {
        console.log("<---- RESPONSE ---->", response)
        return Promise.resolve(response)
    },
    async function (error) {
        console.log("<---- ERROR ---->", error)
        return Promise.reject(error)
    }
)

carePlanAiInstance.interceptors.response.use(
    async function (response) {
        console.log("<----CARE PLAN AI RESPONSE ---->", response)
        return Promise.resolve(response)
    },
    async function (error) {
        console.log("<----CARE PLAN AI ERROR ---->", error)
        return Promise.reject(error)
    }
)

export async function axiosGet(
    url = "",
    headers = {},
    queryParams = {},
    body = {},
    responseType
) {
    if (Object.entries(queryParams).length) {
        url += "?"
        let entries = Object.entries(queryParams)
        for (let param of entries) url += `${param[0]}=${param[1]}&`
    }
    try {
        const response = await healthInstance({
            method: "GET",
            url: url,
            ...(responseType ? { responseType } : {}),
            headers: {
                ...headers,
            },
            body: body,
        })
        console.log(response)
        return response
    } catch (err) {
        console.log(err, "axios get error")
    }
}

export async function axiosPost(url = "", data = {}, headers = {}) {
    try {
        return await healthInstance({
            method: "POST",
            url: url,
            data: data,
            headers: {
                ...headers,
            },
        })
    } catch (err) {
        console.log(err, "axios post error")
    }
}

export async function axiosDocAssistPost(url = "", data = {}, headers = {}) {
    try {
        return await docAssistInstance({
            method: "POST",
            url: url,
            data: data,
            headers: {
                ...headers,
            },
        })
    } catch (err) {
        console.log(err, "axios post error")
    }
}

export function axiosPut(url = "", data = {}, headers = {}) {
    return healthInstance({
        method: "PUT",
        url: url,
        data: data,
        headers: {
            ...headers,
        },
    })
}

export function axiosDelete(url = "", headers = {}) {
    return healthInstance({
        method: "DELETE",
        url: url,
        headers: {
            ...headers,
        },
    })
}

export function axiosPatch(url = "", data = {}, headers = {}) {
    return healthInstance({
        method: "PATCH",
        url: url,
        data: data,
        headers: {
            ...headers,
        },
    })
}

export function axiosGetJournal(url = "", headers = {}) {
    return journalInstance({
        method: "GET",
        url: url,
        headers: {
            ...headers,
        },
    })
}

export function axiosPostJournal(url = "", data = {}, headers = {}) {
    return journalInstance({
        method: "POST",
        url: url,
        data: data,
        headers: {
            ...headers,
        },
    })
}

export function axiosPutJournal(url = "", data = {}, headers = {}) {
    return journalInstance({
        method: "PUT",
        url: url,
        data: data,
        headers: {
            ...headers,
        },
    })
}

export function axiosDeleteJournal(url = "", headers = {}) {
    return journalInstance({
        method: "DELETE",
        url: url,
        headers: {
            ...headers,
        },
    })
}

export function axiosPatchJournal(url = "", data = {}, headers = {}) {
    return journalInstance({
        method: "PATCH",
        url: url,
        data: data,
        headers: {
            ...headers,
        },
    })
}

export function axiosLambdaGet(url = "", headers = {}) {
    console.log("url", url)
    return lambdaInstance({
        method: "GET",
        url: url,
        headers: {
            ...headers,
        },
    })
}

export function axiosGetChatGPT(url = "", headers = {}) {
    console.log("url", url)
    return chatGPTInstance({
        method: "GET",
        url: url,
        headers: {
            ...headers,
        },
    })
}

export function axiosPostChatGPT(url = "", data = {}, headers = {}) {
    return chatGPTInstance({
        method: "POST",
        url: url,
        data: data,
        headers: {
            ...headers,
        },
    })
}

export function axiosGetGPT(url = "", headers = {}) {
    console.log("url", url)
    return gptInstance({
        method: "GET",
        url: url,
        headers: {
            ...headers,
        },
    })
}

export function axiosPostGPT(url = "", data = {}, headers = {}) {
    return gptInstance({
        method: "POST",
        url: url,
        data: data,
        headers: {
            ...headers,
        },
    })
}

export function axiosGetTwilio(url = "", headers = {}) {
    console.log("url", url)
    return twilioInstance({
        method: "GET",
        url: url,
        headers: {
            ...headers,
        },
    })
}

export function axiosPostCarePlanAI(url = "", data = {}, headers = {}) {
    console.log("url", url)
    return carePlanAiInstance({
        method: "POST",
        url: url,
        data: data,
        headers: {
            ...headers,
        },
    })
}

export function axiosDoctoriteAiInstance(
    method = "GET",
    url = "",
    data = {},
    headers = {}
) {
    console.log(url, "url in doc ai instance")
    return DoctoriteAiInstance({
        method: method,
        url: url,
        data: data,
        headers: {
            ...headers,
        },
    })
}

const handleTokenRefresh = () => {
    console.log("<---- REFESH TOKEN ---->")
    // store.dispatch(endLoginTimeTracker(false))
    // store.dispatch(logoutAction())
    // store.dispatch(userLoggedOut())
    sessionStorage.removeItem("patientComponentState")
    localStorage.setItem("user", JSON.stringify({}))
    navigate("/login")
}
